import { Container, Graphics } from "pixi.js";
import { drawFastenerEdgeMark, drawFastenerHead } from "./fastenerHead";
import { PIXELS_PER_INCH } from "../shop-scale";
import {
  defaultPartsFor,
  productBlueprintFor,
  ProductBlueprint,
} from "../../game/bench-work/blueprint";
import { AssembledPart, Board, FinishedProduct } from "../../game/Materials";
import { createBoardSprite, drawBoardOnEdge, drawBoardOnEnd } from "./board";
import { drawPanel } from "./panel";
import { drawSheetGood } from "./sheetGood";

/**
 * A blueprint-assembled product drawn from its bill of materials: the
 * blueprint says where every part lies (the same slots the bench view's
 * ghost outlines stood at), and the product's parts say which board
 * fills each slot — seeded by the consumed board's id, so the grain the
 * player laid on the bench is the grain in the finished piece, at every
 * zoom. Nail heads sit on the fastener points, all driven — it's done.
 *
 * Products from before the bill of materials render the blueprint's
 * nominal stock in the product's species (defaultPartsFor).
 *
 * The old AssembledProductSprite as an imperative builder: returns a
 * Container centered on the product; the caller owns its transform and
 * lifecycle.
 */
export function createAssembledProductSprite(
  material: FinishedProduct,
  options: { alpha?: number; tint?: number } = {},
): Container {
  const blueprint = productBlueprintFor(material.type);
  if (!blueprint) {
    throw new Error(`No blueprint to draw a ${material.type} from`);
  }
  const parts = material.parts ?? defaultPartsFor(blueprint, material);
  return createBlueprintSprite(blueprint, parts, options);
}

/**
 * The drawing itself: a blueprint and the parts filling its slots,
 * centered on the product. `createAssembledProductSprite` calls this
 * for a real product; the blueprint editor calls it with stand-in parts
 * for a plan that isn't saved yet.
 */
export function createBlueprintSprite(
  blueprint: ProductBlueprint,
  parts: ReadonlyArray<AssembledPart>,
  options: { alpha?: number; tint?: number } = {},
): Container {
  const { alpha, tint } = options;
  const root = new Container();
  if (alpha !== undefined) {
    root.alpha = alpha;
  }
  const inner = root.addChild(new Container());
  inner.position.set(
    (-blueprint.widthIn * PIXELS_PER_INCH) / 2,
    (-blueprint.heightIn * PIXELS_PER_INCH) / 2,
  );

  // Lowest part first: what sits higher draws over what it sits on
  const slots = [...blueprint.slots].sort((a, b) => a.zIn - b.zIn);

  for (const slot of slots) {
    const part =
      parts.find((p) => p.slot === slot.id) ??
      // A part list that predates a blueprint revision: draw the
      // slot's nominal stock rather than a hole.
      ({
        slot: slot.id,
        species: "pallet",
        // Nominal part dims are board-catalog sizes on every product
        // blueprint (only equipment blueprints use sheet-size parts,
        // and equipment never renders as a product)
        width: slot.part.widthIn as AssembledPart["width"],
        length: slot.part.lengthIn,
        thickness: slot.part.thicknessQ as AssembledPart["thickness"],
        ...(slot.part.ends ? { ends: slot.part.ends } : {}),
        seed: slot.id,
      } satisfies AssembledPart);
    const board = {
      species: part.species,
      // Panel parts keep their own derived width; board parts are
      // catalog stock, so the widened number narrows back safely
      width: part.width as Board["width"],
      length: part.length,
      thickness: part.thickness,
      surface: part.surface ?? ("rough" as const),
      // A part in a finished piece is as milled as it is ever going to
      // be, so both axes read done and `surface` carries how rough it
      // is on its own. Saying one face here would claim the part came
      // straight off the jointer, and board.ts would draw it the skip
      // that goes with that.
      jointedFaces: 2 as const,
      jointedEdges: 2 as const,
      // A frame rail's mitered ends draw in the finished piece — the
      // corner seams are the product
      ...(part.ends ? { ends: part.ends } : {}),
    };

    const slotContainer = inner.addChild(new Container());
    slotContainer.position.set(
      slot.xIn * PIXELS_PER_INCH,
      slot.yIn * PIXELS_PER_INCH,
    );
    slotContainer.angle = slot.angleDeg;
    /** One Graphics with the shared tint applied. */
    const graphics = (draw: (g: Graphics) => void): void => {
      const g = slotContainer.addChild(new Graphics());
      draw(g);
      if (tint !== undefined) {
        g.tint = tint;
      }
    };
    if (part.sheetKind) {
      // A sheet part is all face: the very veneer the slot's sheet showed
      const sheetKind = part.sheetKind;
      graphics((g) =>
        drawSheetGood(
          g,
          {
            kind: sheetKind,
            width: part.width,
            length: part.length,
            thickness: part.thickness as 1 | 2 | 3 | 4,
          },
          part.seed,
        ),
      );
    } else if (part.strips) {
      // A glued-up part (a tray's bottom) draws as the panel it
      // is — the very stripes the player glued
      const strips = part.strips;
      graphics((g) =>
        drawPanel(
          g,
          {
            strips,
            length: part.length,
            thickness: part.thickness,
            surface: part.surface ?? "sanded",
          },
          part.seed,
        ),
      );
    } else if (slot.onEdge) {
      // A rail stands on edge in the finished piece exactly as
      // it stood while the slats were nailed across it
      graphics((g) => drawBoardOnEdge(g, board, part.seed));
    } else if (slot.onEnd) {
      // A leg stands on its end — the finished table shows the legs'
      // cross-sections against the underside of its top
      graphics((g) => drawBoardOnEnd(g, board, part.seed));
    } else {
      slotContainer.addChild(createBoardSprite(board, part.seed, { tint }));
    }
  }

  const nailsG = inner.addChild(new Graphics());
  for (const fastener of blueprint.fasteners) {
    const x = fastener.xIn * PIXELS_PER_INCH;
    const y = fastener.yIn * PIXELS_PER_INCH;
    if (fastener.level) {
      // Driven from the side: the head is on a face the plan sees
      // edge-on, so it reads as a mark on the outline
      drawFastenerEdgeMark(
        nailsG,
        x,
        y,
        1,
        (fastener.level.headingDeg * Math.PI) / 180,
      );
    } else {
      drawFastenerHead(nailsG, x, y, 1, blueprint.fastenerConsumable);
    }
  }

  return root;
}
